import React, { useRef, useState } from "react";
import api from "../../../../api";

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const UploadTab = ({ onUploaded }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { type: 'success' | 'danger', text }

  const resetFile = () => {
    setFile(null);
    setFileError("");
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    setResult(null);
    if (!picked) {
      resetFile();
      return;
    }

    if (!picked.name.toLowerCase().endsWith(".json")) {
      setFile(null);
      setFileError("Please choose a .json file.");
      return;
    }

    setFileError("Validating…");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        JSON.parse(reader.result);
        setFile(picked);
        setFileError("");
      } catch {
        setFile(null);
        setFileError("This file isn't valid JSON — check it and try again.");
      }
    };
    reader.onerror = () => {
      setFile(null);
      setFileError("Couldn't read this file.");
    };
    reader.readAsText(picked);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;

    setUploading(true);
    setProgress(0);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/api/repertory/upload", formData, {
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });

      setResult({
        type: "success",
        text:
          typeof res.data === "string"
            ? res.data
            : "Repertory JSON uploaded successfully.",
      });
      resetFile();
      onUploaded?.();
    } catch (err) {
      setResult({
        type: "danger",
        text:
          err.response?.data?.message ||
          (typeof err.response?.data === "string" ? err.response.data : null) ||
          "Upload failed. Please check the file and try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h5 className="card-title">Upload Repertory JSON</h5>
        <p className="text-muted small mb-4">
          Upload a repertory export (.json) to add its chapters/lessons and
          rubrics. New lessons will appear in the{" "}
          <strong>Lessons &amp; Chapters</strong> tab once the upload
          finishes.
        </p>

        {result && (
          <div
            className={`alert alert-${result.type} alert-dismissible`}
            role="alert"
          >
            {result.text}
            <button
              type="button"
              className="btn-close"
              onClick={() => setResult(null)}
            />
          </div>
        )}

        <label
          htmlFor="repertory-json-input"
          className="d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded-3 py-5 mb-3 text-center"
          style={{
            cursor: uploading ? "not-allowed" : "pointer",
            borderStyle: "dashed",
            background: "#f8f9fa",
          }}
        >
          <div className="fs-2 mb-2">📄</div>
          {file ? (
            <>
              <div className="fw-semibold">{file.name}</div>
              <div className="text-muted small">{formatBytes(file.size)}</div>
            </>
          ) : (
            <>
              <div className="fw-semibold">Click to choose a .json file</div>
              <div className="text-muted small">
                The file is validated in your browser before upload
              </div>
            </>
          )}
          <input
            id="repertory-json-input"
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="d-none"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>

        {fileError && <p className="text-danger small">{fileError}</p>}

        {uploading && (
          <div className="progress mb-3" style={{ height: "8px" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-success"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Uploading… {progress}%
              </>
            ) : (
              "Upload"
            )}
          </button>
          {file && !uploading && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFile}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadTab;
